import { Avatar, Box, Typography } from "@mui/material";
import ReplyIcon from "@mui/icons-material/Reply";
import BlockIcon from "@mui/icons-material/Block";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { applicantService } from "../../../../services/applicant.service";
import Loading from "../../../common/Loading";
import ADFRenderer from "../../../common/ADFRenderer";

interface Comment {
  id: number;
  author: string;
  commentBody: string;
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  issueId: string;
  jiraCommentId: string;
  syncedAt: string;
  parentJiraCommentId?: string | null;
}

interface JiraCommentsResponse {
  comments: Comment[];
}

interface CommentThread {
  parent: Comment;
  replies: Comment[];
}

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    : name.charAt(0).toUpperCase();
};

const AVATAR_COLORS = [
  "#5656FF", "#FF6B6B", "#4ECDC4", "#45B7D1", "#FFA07A",
  "#98D8C8", "#F7DC6F", "#BB8FCE", "#85C1E2", "#F8B88B",
];

const getColorFromName = (name: string): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatDateTime = (dateString: string): string => {
  try {
    return dayjs(dateString).format("MMMM D, YYYY [at] h:mm A");
  } catch {
    return dateString;
  }
};

const getCommentTime = (comment: Comment): number =>
  new Date(comment.createdAt).getTime();

/**
 * Build threads purely from parentJiraCommentId supplied by the backend.
 * - Root comments (no parentJiraCommentId) are sorted newest first.
 * - Replies under each root are sorted oldest first (chronological).
 * - If a reply's parent is not found (deleted / outside sync window) it is
 *   promoted to a root comment so it is never silently lost.
 */
const buildJiraThreads = (comments: Comment[]): CommentThread[] => {
  if (comments.length === 0) return [];

  // Index every comment by its Jira comment ID for O(1) parent lookup.
  const byJiraId = new Map<string, Comment>();
  for (const c of comments) {
    byJiraId.set(c.jiraCommentId, c);
  }

  const repliesByParentDbId = new Map<number, Comment[]>();
  const rootComments: Comment[] = [];

  // Process in chronological order so replies are naturally oldest→newest.
  const chronological = [...comments].sort((a, b) => {
    const diff = getCommentTime(a) - getCommentTime(b);
    if (diff !== 0) return diff;
    return parseInt(a.jiraCommentId, 10) - parseInt(b.jiraCommentId, 10);
  });

  for (const comment of chronological) {
    const parentJiraId = comment.parentJiraCommentId?.trim();
    if (!parentJiraId) {
      rootComments.push(comment);
      continue;
    }

    const parent = byJiraId.get(parentJiraId);
    if (!parent) {
      // Parent missing (deleted or not yet synced) — promote to root.
      rootComments.push(comment);
      continue;
    }

    const bucket = repliesByParentDbId.get(parent.id) ?? [];
    bucket.push(comment);
    repliesByParentDbId.set(parent.id, bucket);
  }

  // Roots: newest first (matches Jira's default display order).
  const sortedRoots = [...rootComments].sort((a, b) => {
    const diff = getCommentTime(b) - getCommentTime(a);
    if (diff !== 0) return diff;
    return parseInt(a.jiraCommentId, 10) - parseInt(b.jiraCommentId, 10);
  });

  return sortedRoots.map((parent) => ({
    parent,
    replies: (repliesByParentDbId.get(parent.id) ?? []).sort(
      (a, b) => getCommentTime(a) - getCommentTime(b)
    ),
  }));
};

interface CommentItemProps {
  comment: Comment;
  isReply?: boolean;
  parentAuthor?: string | null;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isReply = false,
  parentAuthor = null,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        ml: isReply ? 6 : 0,
        position: "relative",
        ...(isReply && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: -28,
            top: 16,
            width: 20,
            height: 20,
            borderLeft: "2px solid #DFE1E6",
            borderBottom: "2px solid #DFE1E6",
            borderBottomLeftRadius: "6px",
          },
        }),
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          backgroundColor: getColorFromName(comment.author),
          color: "#fff",
          fontWeight: 600,
          fontSize: "13px",
          flexShrink: 0,
        }}
      >
        {getInitials(comment.author)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {isReply && parentAuthor && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "11px",
              color: "#6B778C",
              mb: 0.5,
            }}
          >
            <ReplyIcon sx={{ fontSize: 13 }} />
            replied to&nbsp;
            <Box component="span" sx={{ color: "#0052CC", fontWeight: 500 }}>
              {parentAuthor}
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", mb: 0.5 }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "13.5px",
              color: "#1A1A1A",
              lineHeight: 1.4,
            }}
          >
            {comment.author}
          </Typography>
          <Typography
            sx={{
              fontSize: "11px",
              color: "#999",
              lineHeight: 1.4,
            }}
          >
            {formatDateTime(comment.createdAt)}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: isReply ? "#F8F9FF" : "transparent",
            borderRadius: isReply ? "0 8px 8px 8px" : 0,
            border: isReply ? "1px solid #E8EAFF" : "none",
            px: isReply ? 1.5 : 0,
            py: isReply ? 1 : 0,
          }}
        >
          <ADFRenderer content={comment.commentBody} issueId={comment.issueId} />
        </Box>
      </Box>
    </Box>
  );
};

const Comments: React.FC = () => {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [threads, setThreads] = useState<CommentThread[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const fetchComments = async () => {
      try {
        setIsLoading(true);
        const response =
          await applicantService.getJiraComments<JiraCommentsResponse>(id);
        setThreads(buildJiraThreads(response.data.comments || []));
        setError(null);
      } catch (err) {
        console.error("Error fetching comments:", err);
        setError("Failed to load comments");
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [id]);

  if (isLoading) return <Loading />;

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (threads.length === 0) {
    return (
      <Box sx={{ textAlign: "left", py: 3, px: 2 }}>
        <Typography color="textSecondary">No comments yet</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "20px 24px",
        boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
        width: "1246px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {threads.map((thread) => (
          <Box
            key={thread.parent.id}
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {thread.parent.isDeleted ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    backgroundColor: "#DFE1E6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <BlockIcon sx={{ fontSize: 18, color: "#6B778C" }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#6B778C",
                    fontStyle: "italic",
                    fontFamily: "Inter",
                  }}
                >
                  Comment deleted
                </Typography>
              </Box>
            ) : (
              <CommentItem comment={thread.parent} />
            )}

            {thread.replies.map((reply) =>
              reply.isDeleted ? (
                <Box
                  key={reply.id}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: 6 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor: "#DFE1E6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <BlockIcon sx={{ fontSize: 16, color: "#6B778C" }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#6B778C",
                      fontStyle: "italic",
                      fontFamily: "Inter",
                    }}
                  >
                    Comment deleted
                  </Typography>
                </Box>
              ) : (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  isReply
                  parentAuthor={thread.parent.isDeleted ? null : thread.parent.author}
                />
              )
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Comments;
